import type { DesignTokensPayload } from '../bridge/messages';
import {
  isThemeId,
  normalizeColorSetId,
  type ColorSetId,
  type ThemeId,
} from '../catalog/platformCatalogIds';
import { THEME_REGISTRY } from './themeCatalog';
import { findCustomColorSet, isCustomColorSetId } from './customColorSet';
import {
  isTypographyEnumKey,
  normalizeCornerEnumKey,
  resolveCornerPreset,
  resolveTypographyPreset,
} from './themePresets';
import {
  CORNER_ENUM_KEYS,
  SPACING_ENUM_KEYS,
  TYPOGRAPHY_ENUM_KEYS,
  type ColorSetTokenMap,
  type CornerEnumKey,
  type ProjectThemeOverrides,
  type ResolvedProjectTheme,
  type SpacingEnumKey,
  type ThemeColorSetDefinition,
  type ThemeRegistryEntry,
  type TypographyEnumKey,
} from './types';

export function getThemeRegistryEntry(themeId: string): ThemeRegistryEntry | null {
  if (!isThemeId(themeId)) return null;
  return THEME_REGISTRY[themeId];
}

export function getColorSetsForTheme(themeId: string): ThemeColorSetDefinition[] {
  if (!isThemeId(themeId)) return [];
  const entry = THEME_REGISTRY[themeId];
  if (!entry) return [];
  const reference = entry.reference_color_sets ?? [];
  return reference.length > 0 ? [...entry.color_sets, ...reference] : entry.color_sets;
}

export function resolveDefaultColorSetId(themeId: ThemeId): ColorSetId;
export function resolveDefaultColorSetId(themeId: string): ColorSetId | null;
export function resolveDefaultColorSetId(themeId: string): ColorSetId | null {
  return THEME_REGISTRY[themeId as ThemeId]?.default_colorset_id ?? null;
}

export function isValidThemeColorSet(themeId: string, colorsetId: string): boolean {
  const entry = THEME_REGISTRY[themeId as ThemeId];
  if (!entry) return false;
  const id = normalizeColorSetId(colorsetId);
  if (entry.color_sets.some((set) => set.id === id)) return true;
  return (entry.reference_color_sets ?? []).some((set) => set.id === id);
}

/**
 * Single source of truth: resolve theme + color-set from THEME_REGISTRY
 * (or project customColorSets when colorsetId is `custom_*`).
 * Optional corner/typography enums override theme defaults (same pattern as spacingEnum).
 */
export function resolveProjectColorSet(
  themeId: string,
  colorsetId?: string,
  overrides?: Pick<
    ProjectThemeOverrides,
    'cornerEnum' | 'typographyEnum' | 'customColorSets'
  >,
): ResolvedProjectTheme | null {
  if (!isThemeId(themeId)) return null;
  const entry = THEME_REGISTRY[themeId];
  if (!entry) return null;
  const rawId = colorsetId?.trim() || entry.default_colorset_id;
  const resolvedId = isCustomColorSetId(rawId) ? rawId : normalizeColorSetId(rawId);

  let setId = resolvedId;
  let tokens: ColorSetTokenMap | null = null;

  if (isCustomColorSetId(resolvedId)) {
    const custom = findCustomColorSet(overrides?.customColorSets, resolvedId);
    if (custom) {
      setId = custom.id;
      tokens = custom.tokens;
    }
  }

  if (!tokens) {
    const selectable = [
      ...entry.color_sets,
      ...(entry.reference_color_sets ?? []),
    ];
    const set =
      selectable.find((colorSet) => colorSet.id === resolvedId) ??
      entry.color_sets.find((colorSet) => colorSet.id === entry.default_colorset_id);
    if (!set) return null;
    setId = set.id;
    tokens = set.tokens;
  }

  const cornerEnum =
    normalizeCornerEnumKey(overrides?.cornerEnum) ??
    normalizeCornerEnumKey(entry.corner_enum) ??
    entry.corner_enum;
  const typographyEnum =
    overrides?.typographyEnum && isTypographyEnumKey(overrides.typographyEnum)
      ? overrides.typographyEnum
      : entry.typography_enum;
  const typographyPreset = resolveTypographyPreset(typographyEnum);

  return {
    themeId: entry.themeId,
    colorsetId: setId,
    tokens,
    cornerEnum,
    typographyEnum,
    corner: resolveCornerPreset(cornerEnum),
    typography: {
      ...typographyPreset,
      spacing: entry.typography_tokens.spacing,
    },
  };
}

export function colorSetTokensToDesignPayload(tokens: ColorSetTokenMap): DesignTokensPayload {
  return {
    colors: {
      brandPrimary: tokens.brandPrimary,
      brandAccent: tokens.brandAccent,
    },
  };
}

export function resolveDesignTokensForTheme(
  themeId: string,
  colorsetId: string,
  overrides?: Pick<ProjectThemeOverrides, 'typographyEnum'>,
): DesignTokensPayload | null {
  const resolved = resolveProjectColorSet(themeId, colorsetId, overrides);
  if (!resolved) return null;
  return {
    ...colorSetTokensToDesignPayload(resolved.tokens),
    fonts: {
      brandHeading: resolved.typography.headingFont,
      brandBody: resolved.typography.bodyFont,
    },
  };
}

/** Default layout spacing for a theme — used at onboarding project create. */
export function resolveThemeSpacingEnum(themeId: string): SpacingEnumKey {
  const entry = getThemeRegistryEntry(themeId);
  return entry?.typography_tokens.spacing ?? SPACING_ENUM_KEYS.balanced;
}

/** Default corner preset for a theme — used at onboarding project create. */
export function resolveThemeCornerEnum(themeId: string): CornerEnumKey {
  const entry = getThemeRegistryEntry(themeId);
  return (
    normalizeCornerEnumKey(entry?.corner_enum) ??
    entry?.corner_enum ??
    CORNER_ENUM_KEYS.rounded
  );
}

/** Default typography preset for a theme — used at onboarding project create. */
export function resolveThemeTypographyEnum(themeId: string): TypographyEnumKey {
  const entry = getThemeRegistryEntry(themeId);
  return entry?.typography_enum ?? TYPOGRAPHY_ENUM_KEYS.editorialSerif;
}
